import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import { gsap } from 'gsap';
import { Link, useNavigate } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { showToast } from '../components/Toast';

const Checkout = () => {
  const { cartItems, updateQuantity, removeFromCart } = useCart();
  const navigate = useNavigate();
  const containerRef = useRef();
  const headerRef = useRef();
  const formRef = useRef();
  const summaryRef = useRef();

  // Form states
  const [formData, setFormData] = useState({
    // Contact Information
    email: '',
    phone: '',
    
    // Shipping Address
    firstName: '',
    lastName: '',
    address: '',
    apartment: '',
    city: '',
    state: '',
    zipCode: '',
    country: 'United States',
    
    // Payment Information
    cardNumber: '',
    expiryDate: '',
    cvv: '',
    cardName: '',
    
    // Additional Options
    saveInfo: false,
    sameAsShipping: true,
    shippingMethod: 'standard',
    
    // Billing Address (if different)
    billingFirstName: '',
    billingLastName: '',
    billingAddress: '',
    billingApartment: '',
    billingCity: '',
    billingState: '',
    billingZipCode: '',
    billingCountry: 'United States'
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    // Redirect if cart is empty
    if (cartItems.length === 0) {
      navigate('/cart');
      return;
    }

    // GSAP animations
    gsap.fromTo(headerRef.current,
      { y: -30, opacity: 0 },
      { y: 0, opacity: 1, duration: 0.8, ease: "power2.out" }
    );

    gsap.fromTo(formRef.current,
      { x: -50, opacity: 0 },
      { x: 0, opacity: 1, duration: 1, delay: 0.2, ease: "power2.out" }
    );

    gsap.fromTo(summaryRef.current,
      { x: 50, opacity: 0 },
      { x: 0, opacity: 1, duration: 1, delay: 0.4, ease: "power2.out" }
    );
  }, [cartItems, navigate]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    // Required fields validation
    const requiredFields = [
      'email', 'firstName', 'lastName', 'address', 'city', 'state', 'zipCode',
      'cardNumber', 'expiryDate', 'cvv', 'cardName'
    ];
    
    requiredFields.forEach(field => {
      if (!formData[field].trim()) {
        newErrors[field] = 'This field is required';
      }
    });
    
    // Email validation
    if (formData.email && !/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    
    // Phone validation (optional but if provided should be valid)
    if (formData.phone && !/^\+?[\d\s\-\(\)]+$/.test(formData.phone)) {
      newErrors.phone = 'Please enter a valid phone number';
    }
    
    // Card number validation (basic)
    if (formData.cardNumber && formData.cardNumber.replace(/\s/g, '').length < 13) {
      newErrors.cardNumber = 'Please enter a valid card number';
    }
    
    // Expiry date validation
    if (formData.expiryDate && !/^(0[1-9]|1[0-2])\/([0-9]{2})$/.test(formData.expiryDate)) {
      newErrors.expiryDate = 'Please enter date in MM/YY format';
    }
    
    // CVV validation
    if (formData.cvv && (formData.cvv.length < 3 || formData.cvv.length > 4)) {
      newErrors.cvv = 'CVV must be 3 or 4 digits';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      showToast('Please fill in all required fields correctly', 'error');
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      showToast('Order placed successfully!', 'success');
      
      // Clear cart and redirect to success page
      // For now, just redirect to home
      setTimeout(() => {
        navigate('/', { replace: true });
      }, 1500);
      
    } catch (error) {
      showToast('Failed to process order. Please try again.', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Calculate totals
  const subtotal = cartItems.reduce((sum, item) => sum + item.price * item.quantity, 0);
  const shipping = subtotal >= 100 ? 0 : 8.00;
  const tax = subtotal * 0.08;
  const total = subtotal + shipping + tax;

  const formatCardNumber = (value) => {
    const v = value.replace(/\s+/g, '').replace(/[^0-9]/gi, '');
    const matches = v.match(/\d{4,16}/g);
    const match = matches && matches[0] || '';
    const parts = [];
    for (let i = 0, len = match.length; i < len; i += 4) {
      parts.push(match.substring(i, i + 4));
    }
    if (parts.length) {
      return parts.join(' ');
    } else {
      return v;
    }
  };

  const handleCardNumberChange = (e) => {
    const formatted = formatCardNumber(e.target.value);
    setFormData(prev => ({ ...prev, cardNumber: formatted }));
  };

  const formatExpiryDate = (value) => {
    const v = value.replace(/\D/g, '');
    if (v.length >= 2) {
      return v.substring(0, 2) + '/' + v.substring(2, 4);
    }
    return v;
  };

  const handleExpiryChange = (e) => {
    const formatted = formatExpiryDate(e.target.value);
    setFormData(prev => ({ ...prev, expiryDate: formatted }));
  };

  return (
    <CheckoutContainer ref={containerRef}>
      <CheckoutHeader ref={headerRef}>
        <BackButton as={Link} to="/cart">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M19 12H5M12 19l-7-7 7-7"/>
          </svg>
          Back to Cart
        </BackButton>
        <CheckoutTitle>Secure Checkout</CheckoutTitle>
        <CheckoutSubtitle>Complete your Nike purchase</CheckoutSubtitle>
      </CheckoutHeader>

      <CheckoutMain>
        <CheckoutForm ref={formRef} onSubmit={handleSubmit}>
          {/* Contact Information */}
          <FormSection>
            <SectionTitle>Contact Information</SectionTitle>
            <FormRow>
              <FormGroup>
                <FormLabel>Email Address *</FormLabel>
                <FormInput
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  error={errors.email}
                  placeholder="your@email.com"
                />
                {errors.email && <ErrorText>{errors.email}</ErrorText>}
              </FormGroup>
              <FormGroup>
                <FormLabel>Phone Number</FormLabel>
                <FormInput
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  error={errors.phone}
                  placeholder="+1 (555) 123-4567"
                />
                {errors.phone && <ErrorText>{errors.phone}</ErrorText>}
              </FormGroup>
            </FormRow>
          </FormSection>

          {/* Shipping Address */}
          <FormSection>
            <SectionTitle>Shipping Address</SectionTitle>
            <FormRow>
              <FormGroup>
                <FormLabel>First Name *</FormLabel>
                <FormInput
                  type="text"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleInputChange}
                  error={errors.firstName}
                  placeholder="John"
                />
                {errors.firstName && <ErrorText>{errors.firstName}</ErrorText>}
              </FormGroup>
              <FormGroup>
                <FormLabel>Last Name *</FormLabel>
                <FormInput
                  type="text"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleInputChange}
                  error={errors.lastName}
                  placeholder="Doe"
                />
                {errors.lastName && <ErrorText>{errors.lastName}</ErrorText>}
              </FormGroup>
            </FormRow>
            
            <FormGroup>
              <FormLabel>Address *</FormLabel>
              <FormInput
                type="text"
                name="address"
                value={formData.address}
                onChange={handleInputChange}
                error={errors.address}
                placeholder="123 Main Street"
              />
              {errors.address && <ErrorText>{errors.address}</ErrorText>}
            </FormGroup>
            
            <FormGroup>
              <FormLabel>Apartment, suite, etc.</FormLabel>
              <FormInput
                type="text"
                name="apartment"
                value={formData.apartment}
                onChange={handleInputChange}
                placeholder="Apt 4B (optional)"
              />
            </FormGroup>
            
            <FormRow>
              <FormGroup>
                <FormLabel>City *</FormLabel>
                <FormInput
                  type="text"
                  name="city"
                  value={formData.city}
                  onChange={handleInputChange}
                  error={errors.city}
                  placeholder="New York"
                />
                {errors.city && <ErrorText>{errors.city}</ErrorText>}
              </FormGroup>
              <FormGroup>
                <FormLabel>State *</FormLabel>
                <FormSelect
                  name="state"
                  value={formData.state}
                  onChange={handleInputChange}
                  error={errors.state}
                >
                  <option value="">Select State</option>
                  <option value="NY">New York</option>
                  <option value="CA">California</option>
                  <option value="TX">Texas</option>
                  <option value="FL">Florida</option>
                  {/* Add more states as needed */}
                </FormSelect>
                {errors.state && <ErrorText>{errors.state}</ErrorText>}
              </FormGroup>
              <FormGroup>
                <FormLabel>ZIP Code *</FormLabel>
                <FormInput
                  type="text"
                  name="zipCode"
                  value={formData.zipCode}
                  onChange={handleInputChange}
                  error={errors.zipCode}
                  placeholder="10001"
                />
                {errors.zipCode && <ErrorText>{errors.zipCode}</ErrorText>}
              </FormGroup>
            </FormRow>
          </FormSection>

          {/* Shipping Method */}
          <FormSection>
            <SectionTitle>Shipping Method</SectionTitle>
            <ShippingOptions>
              <ShippingOption>
                <input
                  type="radio"
                  id="standard"
                  name="shippingMethod"
                  value="standard"
                  checked={formData.shippingMethod === 'standard'}
                  onChange={handleInputChange}
                />
                <label htmlFor="standard">
                  <ShippingDetails>
                    <ShippingName>Standard Shipping</ShippingName>
                    <ShippingTime>3-5 business days</ShippingTime>
                  </ShippingDetails>
                  <ShippingPrice>$8.00</ShippingPrice>
                </label>
              </ShippingOption>
              <ShippingOption>
                <input
                  type="radio"
                  id="express"
                  name="shippingMethod"
                  value="express"
                  checked={formData.shippingMethod === 'express'}
                  onChange={handleInputChange}
                />
                <label htmlFor="express">
                  <ShippingDetails>
                    <ShippingName>Express Shipping</ShippingName>
                    <ShippingTime>1-2 business days</ShippingTime>
                  </ShippingDetails>
                  <ShippingPrice>$15.00</ShippingPrice>
                </label>
              </ShippingOption>
            </ShippingOptions>
            {subtotal >= 100 && (
              <FreeShippingNote>🎉 You qualify for free standard shipping!</FreeShippingNote>
            )}
          </FormSection>

          {/* Payment Information */}
          <FormSection>
            <SectionTitle>Payment Information</SectionTitle>
            <FormGroup>
              <FormLabel>Card Number *</FormLabel>
              <FormInput
                type="text"
                name="cardNumber"
                value={formData.cardNumber}
                onChange={handleCardNumberChange}
                error={errors.cardNumber}
                placeholder="1234 5678 9012 3456"
                maxLength="19"
              />
              {errors.cardNumber && <ErrorText>{errors.cardNumber}</ErrorText>}
            </FormGroup>
            
            <FormRow>
              <FormGroup>
                <FormLabel>Expiry Date *</FormLabel>
                <FormInput
                  type="text"
                  name="expiryDate"
                  value={formData.expiryDate}
                  onChange={handleExpiryChange}
                  error={errors.expiryDate}
                  placeholder="MM/YY"
                  maxLength="5"
                />
                {errors.expiryDate && <ErrorText>{errors.expiryDate}</ErrorText>}
              </FormGroup>
              <FormGroup>
                <FormLabel>CVV *</FormLabel>
                <FormInput
                  type="text"
                  name="cvv"
                  value={formData.cvv}
                  onChange={handleInputChange}
                  error={errors.cvv}
                  placeholder="123"
                  maxLength="4"
                />
                {errors.cvv && <ErrorText>{errors.cvv}</ErrorText>}
              </FormGroup>
            </FormRow>
            
            <FormGroup>
              <FormLabel>Name on Card *</FormLabel>
              <FormInput
                type="text"
                name="cardName"
                value={formData.cardName}
                onChange={handleInputChange}
                error={errors.cardName}
                placeholder="John Doe"
              />
              {errors.cardName && <ErrorText>{errors.cardName}</ErrorText>}
            </FormGroup>
          </FormSection>

          {/* Save Information Checkbox */}
          <CheckboxGroup>
            <CheckboxInput
              type="checkbox"
              id="saveInfo"
              name="saveInfo"
              checked={formData.saveInfo}
              onChange={handleInputChange}
            />
            <CheckboxLabel htmlFor="saveInfo">
              Save this information for next time
            </CheckboxLabel>
          </CheckboxGroup>
        </CheckoutForm>

        <OrderSummary ref={summaryRef}>
          <SummaryTitle>Order Summary</SummaryTitle>
          
          <OrderItems>
            {cartItems.map(item => (
              <OrderItem key={`${item.id}-${item.size}-${item.color}`}>
                <ItemImage src={item.image} alt={item.name} />
                <ItemDetails>
                  <ItemName>{item.name}</ItemName>
                  <ItemOptions>
                    {item.size && <span>Size: {item.size}</span>}
                    {item.color && <span>Color: {item.color}</span>}
                    <span>Qty: {item.quantity}</span>
                  </ItemOptions>
                </ItemDetails>
                <ItemPrice>${(item.price * item.quantity).toFixed(2)}</ItemPrice>
              </OrderItem>
            ))}
          </OrderItems>

          <SummaryBreakdown>
            <SummaryLine>
              <span>Subtotal</span>
              <span>${subtotal.toFixed(2)}</span>
            </SummaryLine>
            <SummaryLine>
              <span>Shipping</span>
              <span>{shipping === 0 ? 'FREE' : `$${shipping.toFixed(2)}`}</span>
            </SummaryLine>
            <SummaryLine>
              <span>Tax</span>
              <span>${tax.toFixed(2)}</span>
            </SummaryLine>
            <TotalLine>
              <span>Total</span>
              <span>${total.toFixed(2)}</span>
            </TotalLine>
          </SummaryBreakdown>

          <PlaceOrderButton
            type="submit"
            form="checkout-form"
            disabled={isSubmitting}
            onClick={handleSubmit}
          >
            {isSubmitting ? (
              <>
                <ButtonSpinner />
                Processing...
              </>
            ) : (
              `Place Order - $${total.toFixed(2)}`
            )}
          </PlaceOrderButton>

          <SecurityBadges>
            <SecurityBadge>🔒 SSL Secure</SecurityBadge>
            <SecurityBadge>💳 Safe Payment</SecurityBadge>
            <SecurityBadge>📦 Free Returns</SecurityBadge>
          </SecurityBadges>
        </OrderSummary>
      </CheckoutMain>
    </CheckoutContainer>
  );
};

export default Checkout;

// Styled Components
const CheckoutContainer = styled.div`
  min-height: 100vh;
  background: #ffffff;
  padding-top: 80px;
  
  @media (max-width: 768px) {
    padding-top: 70px;
    width: 100%;
    overflow-x: hidden;
  }
`;

const CheckoutHeader = styled.header`
  background: white;
  padding: 32px 0;
  border-bottom: 1px solid #f0f0f0;
  margin-bottom: 40px;
  width: 100%;
  
  @media (max-width: 768px) {
    padding: 20px 0;
    margin-bottom: 24px;
    width: 100%;
  }
`;

const BackButton = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: #888;
  text-decoration: none;
  font-size: 13px;
  font-weight: 400;
  margin: 0 auto 20px;
  max-width: 1200px;
  padding: 0 20px;
  transition: color 0.2s ease;
  width: 100%;
  
  &:hover {
    color: #111;
  }
  
  svg {
    width: 16px;
    height: 16px;
  }
  
  @media (max-width: 768px) {
    margin-bottom: 16px;
    padding: 0 16px;
    font-size: 12px;
    width: 100%;
    max-width: none;
  }
`;

const CheckoutTitle = styled.h1`
  font-size: 28px;
  font-weight: 600;
  color: #111;
  text-align: center;
  margin: 0 0 6px 0;
  letter-spacing: -0.5px;
  
  @media (max-width: 768px) {
    font-size: 22px;
  }
`;

const CheckoutSubtitle = styled.p`
  color: #999;
  text-align: center;
  margin: 0;
  font-size: 14px;
  font-weight: 400;
  
  @media (max-width: 768px) {
    font-size: 13px;
  }
`;

const CheckoutMain = styled.main`
  max-width: 1100px;
  margin: 0 auto;
  padding: 0 20px;
  display: grid;
  grid-template-columns: 1fr 380px;
  gap: 60px;
  width: 100%;
  
  @media (max-width: 1024px) {
    grid-template-columns: 1fr 340px;
    gap: 40px;
  }
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 32px;
    padding: 0;
    max-width: none;
    width: 100%;
  }
`;

const CheckoutForm = styled.form`
  background: white;
  border-radius: 8px;
  padding: 40px;
  border: 1px solid #f0f0f0;
  height: fit-content;
  width: 100%;
  
  @media (max-width: 768px) {
    padding: 16px;
    border-radius: 0;
    order: 2;
    border: none;
    width: 100vw;
    margin-left: calc(-50vw + 50%);
    box-sizing: border-box;
  }
`;

const FormSection = styled.div`
  margin-bottom: 40px;
  
  @media (max-width: 768px) {
    margin-bottom: 28px;
  }
`;

const SectionTitle = styled.h3`
  font-size: 16px;
  font-weight: 500;
  color: #111;
  margin: 0 0 24px 0;
  padding-bottom: 8px;
  border-bottom: 1px solid #f5f5f5;
  letter-spacing: -0.2px;
  
  @media (max-width: 768px) {
    font-size: 15px;
    margin-bottom: 20px;
  }
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 20px;
  width: 100%;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 16px;
    width: 100%;
  }
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const FormLabel = styled.label`
  font-size: 13px;
  font-weight: 500;
  color: #555;
  margin-bottom: 8px;
  letter-spacing: -0.1px;
`;

const FormInput = styled.input`
  padding: 14px 16px;
  border: 1px solid ${props => props.error ? '#e74c3c' : '#e8e8e8'};
  border-radius: 4px;
  font-size: 14px;
  transition: all 0.2s ease;
  background: #fafafa;
  color: #333;
  width: 100%;
  box-sizing: border-box;
  
  &:focus {
    outline: none;
    border-color: ${props => props.error ? '#e74c3c' : '#111'};
    background: white;
    box-shadow: 0 0 0 1px ${props => props.error ? 'rgba(231, 76, 60, 0.2)' : 'rgba(17, 17, 17, 0.1)'};
  }
  
  &::placeholder {
    color: #bbb;
    font-weight: 400;
  }
  
  @media (max-width: 768px) {
    padding: 16px;
    font-size: 16px; /* Prevents zoom on iOS */
    width: 100%;
  }
`;

const FormSelect = styled.select`
  padding: 14px 16px;
  border: 1px solid ${props => props.error ? '#e74c3c' : '#e8e8e8'};
  border-radius: 4px;
  font-size: 14px;
  background: #fafafa;
  cursor: pointer;
  transition: all 0.2s ease;
  color: #333;
  width: 100%;
  box-sizing: border-box;
  
  &:focus {
    outline: none;
    border-color: ${props => props.error ? '#e74c3c' : '#111'};
    background: white;
    box-shadow: 0 0 0 1px ${props => props.error ? 'rgba(231, 76, 60, 0.2)' : 'rgba(17, 17, 17, 0.1)'};
  }
  
  @media (max-width: 768px) {
    padding: 16px;
    font-size: 16px;
    width: 100%;
  }
`;

const ErrorText = styled.span`
  color: #e74c3c;
  font-size: 11px;
  margin-top: 6px;
  font-weight: 400;
`;

const ShippingOptions = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
`;

const ShippingOption = styled.div`
  border: 1px solid #f0f0f0;
  border-radius: 4px;
  padding: 16px;
  transition: all 0.2s ease;
  background: #fafafa;
  width: 100%;
  box-sizing: border-box;
  
  &:hover {
    border-color: #ddd;
    background: white;
  }
  
  input[type="radio"] {
    margin-right: 12px;
    accent-color: #111;
  }
  
  label {
    display: flex;
    align-items: center;
    justify-content: space-between;
    cursor: pointer;
    width: 100%;
  }
  
  input[type="radio"]:checked + label {
    font-weight: 500;
  }
`;

const ShippingDetails = styled.div`
  display: flex;
  flex-direction: column;
`;

const ShippingName = styled.span`
  font-size: 13px;
  font-weight: 500;
  color: #333;
`;

const ShippingTime = styled.span`
  font-size: 11px;
  color: #888;
  margin-top: 2px;
`;

const ShippingPrice = styled.span`
  font-weight: 500;
  color: #111;
  font-size: 13px;
`;

const FreeShippingNote = styled.div`
  background: #f0f8f0;
  border: 1px solid #e0f0e0;
  color: #2d5a2d;
  padding: 12px 16px;
  border-radius: 4px;
  font-size: 12px;
  margin-top: 12px;
  font-weight: 400;
`;

const CheckboxGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 20px;
`;

const CheckboxInput = styled.input`
  margin: 0;
  accent-color: #111;
`;

const CheckboxLabel = styled.label`
  font-size: 13px;
  color: #555;
  cursor: pointer;
  font-weight: 400;
`;

const OrderSummary = styled.div`
  background: #fafafa;
  border-radius: 8px;
  padding: 32px;
  border: 1px solid #f0f0f0;
  height: fit-content;
  position: sticky;
  top: 120px;
  width: 100%;
  
  @media (max-width: 768px) {
    padding: 16px;
    border-radius: 0;
    position: static;
    order: 1;
    background: #fafafa;
    border: none;
    border-top: 1px solid #f0f0f0;
    border-bottom: 1px solid #f0f0f0;
    width: 100vw;
    margin-left: calc(-50vw + 50%);
    box-sizing: border-box;
  }
`;

const SummaryTitle = styled.h3`
  font-size: 16px;
  font-weight: 500;
  color: #111;
  margin: 0 0 20px 0;
  padding-bottom: 12px;
  border-bottom: 1px solid #f0f0f0;
  letter-spacing: -0.2px;
  
  @media (max-width: 768px) {
    font-size: 15px;
    margin-bottom: 16px;
    position: relative;
    
    &::after {
      content: '← Swipe to see all items →';
      display: block;
      font-size: 10px;
      color: #999;
      font-weight: 400;
      text-align: center;
      margin-top: 8px;
      opacity: 0.7;
    }
  }
`;

const OrderItems = styled.div`
  margin-bottom: 24px;
  
  @media (max-width: 768px) {
    display: flex;
    overflow-x: auto;
    gap: 12px;
    padding: 8px 0;
    margin-bottom: 20px;
    
    /* Hide scrollbar but keep functionality */
    scrollbar-width: none; /* Firefox */
    -ms-overflow-style: none; /* IE and Edge */
    
    &::-webkit-scrollbar {
      display: none; /* Chrome, Safari, Opera */
    }
    
    /* Add fade effect at edges */
    &::before,
    &::after {
      content: '';
      position: absolute;
      top: 0;
      width: 20px;
      height: 100%;
      pointer-events: none;
      z-index: 1;
    }
    
    &::before {
      left: 0;
      background: linear-gradient(to right, #fafafa, transparent);
    }
    
    &::after {
      right: 0;
      background: linear-gradient(to left, #fafafa, transparent);
    }
  }
`;

const OrderItem = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 0;
  border-bottom: 1px solid #f5f5f5;
  
  &:last-child {
    border-bottom: none;
  }
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: center;
    background: white;
    border-radius: 12px;
    padding: 16px;
    border: 1px solid #f0f0f0;
    border-bottom: 1px solid #f0f0f0;
    min-width: 140px;
    max-width: 140px;
    flex-shrink: 0;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
    text-align: center;
    gap: 8px;
    
    &:last-child {
      border-bottom: 1px solid #f0f0f0;
    }
    
    &:hover {
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
    }
  }
`;

const ItemImage = styled.img`
  width: 50px;
  height: 50px;
  object-fit: contain;
  border-radius: 4px;
  background: #f5f5f5;
  
  @media (max-width: 768px) {
    width: 80px;
    height: 80px;
    border-radius: 8px;
    background: #f8f8f8;
    margin-bottom: 4px;
  }
`;

const ItemDetails = styled.div`
  flex: 1;
  
  @media (max-width: 768px) {
    flex: none;
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 4px;
  }
`;

const ItemName = styled.div`
  font-weight: 500;
  color: #111;
  margin-bottom: 3px;
  font-size: 13px;
  letter-spacing: -0.1px;
  
  @media (max-width: 768px) {
    font-size: 12px;
    font-weight: 600;
    margin-bottom: 2px;
    text-align: center;
    line-height: 1.2;
    overflow: hidden;
    text-overflow: ellipsis;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    max-height: 2.4em;
  }
`;

const ItemOptions = styled.div`
  font-size: 11px;
  color: #888;
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  
  span {
    white-space: nowrap;
  }
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: center;
    gap: 2px;
    font-size: 10px;
    color: #666;
    margin-bottom: 6px;
    
    span {
      background: #f5f5f5;
      padding: 2px 6px;
      border-radius: 8px;
      font-weight: 500;
      font-size: 9px;
    }
  }
`;

const ItemPrice = styled.div`
  font-weight: 500;
  color: #111;
  font-size: 13px;
  
  @media (max-width: 768px) {
    font-size: 14px;
    font-weight: 700;
    color: #111;
    background: #f8f8f8;
    padding: 4px 8px;
    border-radius: 6px;
    margin-top: auto;
  }
`;

const SummaryBreakdown = styled.div`
  padding: 16px 0;
  border-top: 1px solid #f0f0f0;
  border-bottom: 1px solid #f0f0f0;
  margin-bottom: 20px;
`;

const SummaryLine = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
  font-size: 13px;
  color: #555;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const TotalLine = styled(SummaryLine)`
  font-weight: 600;
  font-size: 15px;
  color: #111;
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid #f0f0f0;
`;

const PlaceOrderButton = styled.button`
  width: 100%;
  background: #111;
  color: white;
  border: none;
  padding: 16px 24px;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin-bottom: 16px;
  letter-spacing: -0.1px;
  
  &:hover:not(:disabled) {
    background: #000;
  }
  
  &:disabled {
    background: #999;
    cursor: not-allowed;
  }
  
  @media (max-width: 768px) {
    padding: 18px 24px;
    font-size: 15px;
  }
`;

const ButtonSpinner = styled.div`
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top: 2px solid white;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const SecurityBadges = styled.div`
  display: flex;
  justify-content: center;
  gap: 12px;
  flex-wrap: wrap;
  margin-top: 8px;
  
  @media (max-width: 768px) {
    gap: 8px;
  }
`;

const SecurityBadge = styled.div`
  font-size: 11px;
  color: #888;
  display: flex;
  align-items: center;
  gap: 3px;
  font-weight: 400;
`;
